import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { HelpCircle, Upload, FileText, Search, X, Tag, SortAsc, LogOut, Settings, Zap, Ruler, ChevronDown, MoreVertical } from 'lucide-react';
import { Logo } from '@/components/ui/logo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useOfferStore, useQuoteTotals, useFilteredPositions } from '@/lib/store';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import SettingsModal from './settings-modal';
import { debounce } from '@/lib/utils';
import { Position } from '@shared/schema';
import { 
  Dialog as QuoteDialog,
  DialogContent as QuoteDialogContent,
  DialogHeader as QuoteDialogHeader,
  DialogTitle as QuoteDialogTitle, 
  DialogTrigger as QuoteDialogTrigger
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CustomerCombobox, SoftecClient } from './customer-combobox';
import { TextBausteinPicker } from '@/components/text-baustein-picker';
import { AIRewriteButton } from '@/components/ui/ai-rewrite-button';
import { GrosstextDialog } from '@/components/ui/grosstext-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { resolveSelectedCompanyProfileId } from '@shared/company-profile-selection';
import { requestNachtragNumber } from '@/lib/quote-number';

interface HeaderProps {
  onReturnToUpload?: () => void;
  showNavigation?: boolean;
}

export default function Header({ onReturnToUpload, showNavigation = false }: HeaderProps) {
  const [location, navigate] = useLocation();
  const headerRef = React.useRef<HTMLElement>(null);

  // Reale Header-Höhe messen und als CSS-Variable bereitstellen, damit der
  // Content-Offset (.fixed-header-layout) nie hinter dem fixen Header liegt.
  // Vor der ersten Messung gilt der CSS-Fallback --header-height-base.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const applyHeight = () => {
      document.documentElement.style.setProperty(
        '--header-height-current',
        `${el.getBoundingClientRect().height}px`
      );
    };

    applyHeight();
    const observer = new ResizeObserver(applyHeight);
    observer.observe(el);

    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty('--header-height-current');
    };
  }, []);
  const { logout, sessionVia, userName, hasInvoiceAccess } = useAuth();
  const { toast } = useToast();
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const isDataLoaded = useOfferStore((state) => state.isDataLoaded);
  const isGAEBDatabase = useOfferStore((state) => state.isGAEBDatabase);
  const kalkulationsView = useOfferStore((state) => state.kalkulationsView);
  const setKalkulationsView = useOfferStore((state) => state.setKalkulationsView);
  const aufmassView = useOfferStore((state) => state.aufmassView);
  const setAufmassView = useOfferStore((state) => state.setAufmassView);
  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const currentQuoteSlot = useOfferStore((state) => state.currentQuoteSlot);
  const updateQuoteHeader = useOfferStore((state) => state.updateQuoteHeader);
  const quoteRewriteScope = currentQuoteSlot !== null
    ? `quote:slot:${currentQuoteSlot}`
    : `quote:number:${quoteHeader.angebot_nr.trim() || 'draft'}`;
  const totals = useQuoteTotals();
  const totalNet = Object.values(totals.taxGroups).reduce((sum, group) => sum + group.net, 0);
  const totalTax = Object.values(totals.taxGroups).reduce((sum, group) => sum + group.tax, 0);
  const totalGross = totalNet + totalTax;
  const { total: totalPositions } = useFilteredPositions();

  // App settings (company name from settings)
  const [companyName, setCompanyName] = useState<string>('Fortuna Softec');

  // Firmen-Briefkopf-Profile (z.B. Fortuna Elektro / Fortuna Bautec)
  const [companyProfiles, setCompanyProfiles] = useState<Array<{ id: string; label: string }>>([]);
  const [activeProfileId, setActiveProfileId] = useState<string | undefined>(undefined);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Load app settings on mount
  useEffect(() => {
    fetch('/api/pdf-settings')
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        if (data.success && data.data?.companyName) {
          setCompanyName(data.data.companyName);
        }
        if (data.success && Array.isArray(data.data?.profiles)) {
          setCompanyProfiles(data.data.profiles);
        }
        if (data.success && data.data?.activeProfileId) {
          setActiveProfileId(data.data.activeProfileId);
        }
        setSettingsLoaded(true);
      })
      .catch(() => {});
  }, []);

  // Welche Firma wird für dieses Angebot verwendet (für den Hinweis-Badge)?
  const selectedCompanyProfileId = resolveSelectedCompanyProfileId(
    companyProfiles,
    quoteHeader.companyProfileId,
    activeProfileId,
  );
  const selectedCompanyProfile = companyProfiles.find((profile) => profile.id === selectedCompanyProfileId);
  const selectedCompanyLabel = selectedCompanyProfile?.label ?? null;

  // Die tatsächlich angezeigte Auswahl nach erfolgreichem Settings-Load
  // persistieren. Das repariert auch inzwischen gelöschte Profil-IDs, damit
  // Autosave, UI und Rechnungs-Export dieselbe Firma verwenden.
  useEffect(() => {
    if (!settingsLoaded || !isDataLoaded) return;
    if (quoteHeader.companyProfileId === selectedCompanyProfileId) return;
    updateQuoteHeader('companyProfileId', selectedCompanyProfileId ?? '');
  }, [settingsLoaded, isDataLoaded, quoteHeader.companyProfileId, selectedCompanyProfileId, updateQuoteHeader]);

  // Logout confirmation state
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Konto-/Mehr-Menü (Welle 1.5 UX-Entlastung — Kopfzeile entstapeln):
  // Einstellungen und Hilfe sind jetzt Menüpunkte im gebündelten Konto-Menü,
  // ihre Dialoge laufen als GESCHWISTER des DropdownMenu (Controlled-Open,
  // gleiches Muster wie GAEBExport/X84PriceImport im Export-Menü, siehe
  // quote-sidebar.tsx) — so überlebt der Dialog das Schließen des Menüs.
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  // Quote dialog state
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [hasOpenedQuoteDialog, setHasOpenedQuoteDialog] = useState(false);

  // Nachtrag zu einem Basisangebot: Auswahl vorhandener Angebotsnummern +
  // Vergabe der Nachtragsnummer <Basis>/N<n> über den Nummernkreis-Server.
  const [nachtragBaseOptions, setNachtragBaseOptions] = useState<string[]>([]);
  const [selectedNachtragBase, setSelectedNachtragBase] = useState('');
  const [isNumberingNachtrag, setIsNumberingNachtrag] = useState(false);

  useEffect(() => {
    if (!showQuoteDialog) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch('/api/quote-data');
        const result = await response.json();
        if (cancelled || !result?.success) return;
        const files: Array<{ offerNumber?: string }> = result?.data?.files || [];
        const current = (useOfferStore.getState().quoteHeader.angebot_nr || '').trim();
        const options = Array.from(
          new Set(
            files
              .map((f) => (f.offerNumber || '').trim())
              .filter((nr) => nr && nr !== current && !nr.startsWith('Entwurf-')),
          ),
        ).sort((a, b) => a.localeCompare(b, 'de'));
        setNachtragBaseOptions(options);
      } catch {
        // Liste nicht ladbar → Auswahl bleibt leer, Dialog funktioniert weiter.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showQuoteDialog]);

  const handleApplyNachtrag = async () => {
    if (!selectedNachtragBase) return;
    setIsNumberingNachtrag(true);
    try {
      const nummer = await requestNachtragNumber(selectedNachtragBase);
      if (!nummer) {
        toast({
          title: 'Nachtragsnummer fehlgeschlagen',
          description: 'Der Nummernkreis-Server ist nicht erreichbar.',
          variant: 'destructive',
        });
        return;
      }
      updateQuoteHeader('angebot_nr', nummer);
      updateQuoteHeader('nachtragZu', selectedNachtragBase);
      toast({
        title: `Nachtrag ${nummer}`,
        description: `Dieses Angebot ist jetzt Nachtrag zu ${selectedNachtragBase}.`,
      });
    } finally {
      setIsNumberingNachtrag(false);
    }
  };
  const [showTextBausteine, setShowTextBausteine] = useState(false);

  // Check if quote header is incomplete (for glowing effect)
  const isQuoteIncomplete = !quoteHeader.kunde || !quoteHeader.angebot_nr || !quoteHeader.datum ||
                            !quoteHeader.anschrift || !quoteHeader.bauvorhaben || !quoteHeader.lz;

  // Only pulse when data is incomplete AND user hasn't opened the dialog yet
  const shouldPulse = isQuoteIncomplete && !hasOpenedQuoteDialog;
  
  // Search state and functions
  const searchTerm = useOfferStore((state) => state.searchTerm);
  const nameSearchTerm = useOfferStore((state) => state.nameSearchTerm);
  const setSearchTerm = useOfferStore((state) => state.setSearchTerm);
  const setNameSearchTerm = useOfferStore((state) => state.setNameSearchTerm);
  const resetSearch = useOfferStore((state) => state.resetSearch);
  const setSort = useOfferStore((state) => state.setSort);
  

  
  // Local state for input fields
  const [generalInputValue, setGeneralInputValue] = useState(searchTerm);
  const [nameInputValue, setNameInputValue] = useState(nameSearchTerm);
  
  // Update inputs when store values change
  useEffect(() => {
    setGeneralInputValue(searchTerm);
  }, [searchTerm]);
  
  useEffect(() => {
    setNameInputValue(nameSearchTerm);
  }, [nameSearchTerm]);
  
  // Debounced search functions
  const debouncedGeneralSearch = React.useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 300),
    [setSearchTerm]
  );
  
  const debouncedNameSearch = React.useCallback(
    debounce((value: string) => {
      setNameSearchTerm(value);
    }, 300),
    [setNameSearchTerm]
  );
  
  // Handle input changes
  const handleGeneralChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGeneralInputValue(value);
    debouncedGeneralSearch(value);
  };
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameInputValue(value);
    debouncedNameSearch(value);
  };
  
  // Handle reset
  const handleReset = () => {
    resetSearch();
    setGeneralInputValue('');
    setNameInputValue('');
  };
  

  
  // Table column definitions matching the actual table structure

  
  return (
    <TooltipProvider>
      <header ref={headerRef} className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm z-50 transition-all duration-200">
        <div className="px-4 py-2">
          {/* Main header row */}
          <div className="flex justify-between items-center">
            {/* Left side - Logo and title */}
            <div className="flex items-center">
              <Logo height={36} className="mr-4 transition-transform hover:scale-105" />
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-800">{companyName}</h1>
                <p className="text-sm text-gray-600">Angebot & Rechnung</p>
                {/* Angebotsnummer klein anzeigen wenn vorhanden */}
                {showNavigation && isDataLoaded && quoteHeader.angebot_nr && (
                  <p className="text-xs text-blue-600 font-medium">
                    Nr: {quoteHeader.angebot_nr}
                  </p>
                )}
                {/* Aktive Firma / Briefkopf gut sichtbar anzeigen, damit der bewusste
                    Wechsel zwischen den Firmen nicht untergeht */}
                {showNavigation && isDataLoaded && companyProfiles.length > 0 && selectedCompanyLabel && (
                  <Badge
                    variant="outline"
                    className="mt-0.5 w-fit bg-indigo-50 border-indigo-300 text-indigo-700 text-[10px] font-semibold uppercase tracking-wide"
                    data-testid="badge-active-company-profile"
                  >
                    Briefkopf: {selectedCompanyLabel}
                  </Badge>
                )}
              </div>
              <div className="flex ml-6 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                  location === '/' || location.startsWith('/voice') || location.startsWith('/database') || location.startsWith('/excel') || location.startsWith('/gaeb') || location.startsWith('/pdf-lv')
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                >
                  Angebote
                </button>
                {hasInvoiceAccess ? (
                  <button
                    type="button"
                    onClick={() => navigate('/invoices')}
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    location === '/invoices'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  >
                    Rechnungen
                  </button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        disabled
                        aria-disabled="true"
                        className="px-3 py-1.5 text-sm font-medium border-b-2 border-transparent text-gray-300 cursor-not-allowed bg-transparent"
                      >
                        Rechnungen
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rechnungen nur per SSO-Zugang über Softec verfügbar</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {hasInvoiceAccess && (
                  <button
                    type="button"
                    onClick={() => navigate('/leistungsstand')}
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    location === '/leistungsstand'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  >
                    Leistungsstand
                  </button>
                )}
              </div>
            </div>
            
            {/* Center - Angebotsdaten always visible, search only outside GAEB mode */}
            {showNavigation && isDataLoaded && (
              <div className="hidden lg:flex items-center gap-4 flex-1 max-w-5xl mx-8">
                {/* Angebotsdaten Button - Glowing Orange */}
                <QuoteDialog open={showQuoteDialog} onOpenChange={(open) => {
                    setShowQuoteDialog(open);
                    if (open) setHasOpenedQuoteDialog(true);
                  }}>
                  <QuoteDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={`transition-all duration-300 flex-shrink-0 ${
                        isQuoteIncomplete
                          ? shouldPulse
                            ? 'animate-slow-pulse bg-gradient-to-r from-orange-100 to-amber-100 border-orange-300 text-orange-700 shadow-lg shadow-orange-200/50 hover:from-orange-200 hover:to-amber-200'
                            : 'bg-gradient-to-r from-orange-100 to-amber-100 border-orange-300 text-orange-700 hover:from-orange-200 hover:to-amber-200'
                          : 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300 text-green-700 hover:from-green-200 hover:to-emerald-200'
                      }`}
                    >
                      {isQuoteIncomplete ? 'Angebotsdaten' : 'Angebotsdaten'}
                    </Button>
                  </QuoteDialogTrigger>
                  <QuoteDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <QuoteDialogHeader>
                      <QuoteDialogTitle>Angebotsdaten bearbeiten</QuoteDialogTitle>
                    </QuoteDialogHeader>
                    <div className="space-y-4">
                      {/* Erste Reihe - Angebots-Nr. und Datum */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="angebot_nr">Angebots-Nr.</Label>
                          <Input
                            id="angebot_nr"
                            value={quoteHeader.angebot_nr}
                            onChange={(e) => updateQuoteHeader('angebot_nr', e.target.value)}
                            placeholder="wird automatisch vergeben"
                          />
                        </div>

                        <div>
                          <Label htmlFor="datum">Berlin, Datum</Label>
                          <Input
                            id="datum"
                            type="date"
                            value={quoteHeader.datum}
                            onChange={(e) => updateQuoteHeader('datum', e.target.value)}
                          />
                        </div>
                      </div>

                      {/* Nachtrag zu einem Basisangebot */}
                      <div className="rounded-md border border-dashed border-gray-300 p-3 space-y-2">
                        {quoteHeader.nachtragZu ? (
                          <p className="text-xs text-indigo-600 font-medium" data-testid="text-nachtrag-zu">
                            Dieses Angebot ist Nachtrag zu {quoteHeader.nachtragZu}.
                          </p>
                        ) : (
                          <p className="text-xs text-gray-500">
                            Dieses Angebot ist ein Nachtrag zu … (Nummer wird als
                            &lt;Basis&gt;/N&lt;n&gt; vergeben, z.B. {quoteHeader.angebot_nr?.trim() ? `${quoteHeader.angebot_nr}/N1` : 'A26/00025/N1'})
                          </p>
                        )}
                        <div className="flex items-end gap-2">
                          <div className="flex-1">
                            <Select
                              value={selectedNachtragBase}
                              onValueChange={setSelectedNachtragBase}
                              disabled={nachtragBaseOptions.length === 0}
                            >
                              <SelectTrigger data-testid="select-nachtrag-base">
                                <SelectValue placeholder={
                                  nachtragBaseOptions.length === 0
                                    ? 'Keine anderen Angebote vorhanden'
                                    : 'Basisangebot wählen...'
                                } />
                              </SelectTrigger>
                              <SelectContent>
                                {nachtragBaseOptions.map((nr) => (
                                  <SelectItem key={nr} value={nr}>{nr}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleApplyNachtrag}
                            disabled={!selectedNachtragBase || isNumberingNachtrag}
                            data-testid="button-apply-nachtrag"
                          >
                            {isNumberingNachtrag ? 'Nummeriere…' : 'Als Nachtrag nummerieren'}
                          </Button>
                        </div>
                      </div>

                      {/* Firma / Briefkopf - nur anzeigen, wenn Profile konfiguriert sind */}
                      {companyProfiles.length > 0 && (
                        <div>
                          <Label htmlFor="companyProfileId">Firma / Briefkopf</Label>
                          <Select
                            value={selectedCompanyProfileId}
                            onValueChange={(value) => updateQuoteHeader('companyProfileId', value)}
                          >
                            <SelectTrigger id="companyProfileId" data-testid="select-company-profile">
                              <SelectValue placeholder="Firma wählen..." />
                            </SelectTrigger>
                            <SelectContent>
                              {companyProfiles.map((profile) => (
                                <SelectItem key={profile.id} value={profile.id}>
                                  {profile.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-indigo-600 font-medium mt-1">
                            Dieses Angebot wird mit dem Briefkopf „{
                              selectedCompanyLabel
                            }" erstellt.
                          </p>
                        </div>
                      )}

                      {/* Kunde */}
                      <div>
                        <Label htmlFor="kunde">Kunde</Label>
                        <CustomerCombobox
                          value={quoteHeader.kunde}
                          onChange={(value) => updateQuoteHeader('kunde', value)}
                          placeholder="Kunde auswählen..."
                          onSelect={(client: SoftecClient | null) => {
                            if (client) {
                              updateQuoteHeader('kunde', client.name);
                              // Auto-fill address if available
                              if (client.address) {
                                const parts: string[] = [];
                                if (client.address.street) {
                                  parts.push(`${client.address.street}${client.address.houseNumber ? ` ${client.address.houseNumber}` : ''}`);
                                }
                                if (client.address.zip || client.address.city) {
                                  parts.push(`${client.address.zip || ''} ${client.address.city || ''}`.trim());
                                }
                                if (parts.length > 0) {
                                  updateQuoteHeader('anschrift', parts.join('\n'));
                                }
                              }
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">Klicken um Kunden aus Softec zu suchen</p>
                      </div>
                      
                      {/* Anschrift */}
                      <div>
                        <Label htmlFor="anschrift">Anschrift</Label>
                        <Textarea
                          id="anschrift"
                          value={quoteHeader.anschrift || ''}
                          onChange={(e) => updateQuoteHeader('anschrift', e.target.value)}
                          placeholder="Straße, Hausnummer&#10;PLZ Ort"
                          rows={2}
                        />
                      </div>

                      {/* Adresszusatz */}
                      <div>
                        <Label htmlFor="adresszusatz">Adresszusatz</Label>
                        <Input
                          id="adresszusatz"
                          value={quoteHeader.adresszusatz || ''}
                          onChange={(e) => updateQuoteHeader('adresszusatz', e.target.value)}
                          placeholder="z.B. c/o, z.Hd., Abteilung..."
                        />
                      </div>

                      {/* Bauvorhaben */}
                      <div>
                        <Label htmlFor="bauvorhaben">Bauvorhaben</Label>
                        <Textarea
                          id="bauvorhaben"
                          value={quoteHeader.bauvorhaben}
                          onChange={(e) => updateQuoteHeader('bauvorhaben', e.target.value)}
                          placeholder="Beschreibung des Bauvorhabens..."
                          rows={3}
                        />
                      </div>
                      
                      {/* Gewerk und Auftrag */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="gewerk">Gewerk</Label>
                          <Input
                            id="gewerk"
                            value={quoteHeader.gewerk || ''}
                            onChange={(e) => updateQuoteHeader('gewerk', e.target.value)}
                            placeholder="z.B. Elektroinstallation"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="auftrag">Auftrag</Label>
                          <Input
                            id="auftrag"
                            value={quoteHeader.auftrag || ''}
                            onChange={(e) => updateQuoteHeader('auftrag', e.target.value)}
                            placeholder="Auftragsnummer"
                          />
                        </div>
                      </div>
                      
                      {/* Eingereichte Unterlagen */}
                      <div>
                        <Label htmlFor="eingereichte_unterlagen">Eingereichte Unterlagen</Label>
                        <Textarea
                          id="eingereichte_unterlagen"
                          value={quoteHeader.eingereichte_unterlagen || ''}
                          onChange={(e) => updateQuoteHeader('eingereichte_unterlagen', e.target.value)}
                          placeholder="Liste der eingereichten Unterlagen..."
                          rows={2}
                        />
                      </div>
                      
                      {/* LZ (Leistungszeitraum) */}
                      <div>
                        <Label htmlFor="lz">LZ (Leistungszeitraum)</Label>
                        <Input
                          id="lz"
                          value={quoteHeader.lz || ''}
                          onChange={(e) => updateQuoteHeader('lz', e.target.value)}
                          placeholder="z.B. 01.05.2026 - 31.05.2026"
                        />
                      </div>
                      
                      {/* Vor- und Schlusstext (VOB-Textbausteine) */}
                      <Collapsible open={showTextBausteine} onOpenChange={setShowTextBausteine}>
                        <CollapsibleTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="w-full justify-between px-0 text-gray-600 hover:bg-transparent hover:text-gray-900"
                          >
                            <span>Vor- und Schlusstext</span>
                            <ChevronDown
                              className={`h-4 w-4 transition-transform ${showTextBausteine ? 'rotate-180' : ''}`}
                            />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="space-y-4 pt-2">
                          <div>
                            <div className="flex items-center justify-between">
                              <Label htmlFor="vortext">Vortext (vor den Positionen)</Label>
                              <div className="flex items-center gap-1.5">
                                <AIRewriteButton
                                  text={quoteHeader.vortext || ''}
                                  onRewritten={(value) => updateQuoteHeader('vortext', value)}
                                  scopeKey={`${quoteRewriteScope}:vortext`}
                                />
                                <TextBausteinPicker
                                  kategorie="vortext"
                                  currentValue={quoteHeader.vortext || ''}
                                  onInsert={(value) => updateQuoteHeader('vortext', value)}
                                />
                                <GrosstextDialog
                                  value={quoteHeader.vortext || ''}
                                  onChange={(value) => updateQuoteHeader('vortext', value)}
                                  scopeKey={`${quoteRewriteScope}:vortext`}
                                  label="Vortext bearbeiten"
                                  kategorie="vortext"
                                />
                              </div>
                            </div>
                            <Textarea
                              id="vortext"
                              value={quoteHeader.vortext || ''}
                              onChange={(e) => updateQuoteHeader('vortext', e.target.value)}
                              placeholder="Ersetzt den Standardgruß im Angebots-PDF..."
                              rows={3}
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between">
                              <Label htmlFor="schlusstext">Schlusstext (nach der Summe)</Label>
                              <div className="flex items-center gap-1.5">
                                <AIRewriteButton
                                  text={quoteHeader.schlusstext || ''}
                                  onRewritten={(value) => updateQuoteHeader('schlusstext', value)}
                                  scopeKey={`${quoteRewriteScope}:schlusstext`}
                                />
                                <TextBausteinPicker
                                  kategorie="schlusstext"
                                  currentValue={quoteHeader.schlusstext || ''}
                                  onInsert={(value) => updateQuoteHeader('schlusstext', value)}
                                />
                                <GrosstextDialog
                                  value={quoteHeader.schlusstext || ''}
                                  onChange={(value) => updateQuoteHeader('schlusstext', value)}
                                  scopeKey={`${quoteRewriteScope}:schlusstext`}
                                  label="Schlusstext bearbeiten"
                                  kategorie="schlusstext"
                                />
                              </div>
                            </div>
                            <Textarea
                              id="schlusstext"
                              value={quoteHeader.schlusstext || ''}
                              onChange={(e) => updateQuoteHeader('schlusstext', e.target.value)}
                              placeholder="Wird nach der Summen-/Schlusssektion im Angebots-PDF ausgegeben..."
                              rows={3}
                            />
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      <Button
                        onClick={() => {
                          setShowQuoteDialog(false);
                        }}
                        className="w-full"
                      >
                        Speichern
                      </Button>
                    </div>
                  </QuoteDialogContent>
                </QuoteDialog>

                {!isGAEBDatabase && (
                  <>
                    {/* General Search - Made smaller */}
                    <div className="relative flex-1 min-w-0">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            type="text"
                            placeholder="Allgemeine Suche..."
                            className="pl-10 pr-10 h-9 text-sm border border-gray-200 focus:border-blue-400"
                            value={generalInputValue}
                            onChange={handleGeneralChange}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Sucht in allen Feldern: Pos.-Nr., Gewerk, Name, Beschreibung, Einheit und Preis</p>
                        </TooltipContent>
                      </Tooltip>
                      
                      {generalInputValue && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setGeneralInputValue('');
                              debouncedGeneralSearch('');
                            }}
                          >
                            <X className="h-3 w-3 text-gray-400" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Name-Only Search - Made smaller */}
                    <div className="relative w-48">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Tag className="h-4 w-4 text-blue-400" />
                      </div>
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            type="text"
                            placeholder="Name-Suche..."
                            className="pl-10 pr-10 border border-blue-200 focus:border-blue-400 h-9 text-sm"
                            value={nameInputValue}
                            onChange={handleNameChange}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Sucht nur in der Name-Spalte für präzise Ergebnisse</p>
                        </TooltipContent>
                      </Tooltip>
                      
                      {nameInputValue && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setNameInputValue('');
                              debouncedNameSearch('');
                            }}
                          >
                            <X className="h-3 w-3 text-blue-400" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Reset Button - Made smaller */}
                    {(generalInputValue || nameInputValue) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                        className="px-3 h-9 flex-shrink-0 hover:bg-red-50 hover:border-red-300 transition-colors"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Reset
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Center - Navigation summary (when data is loaded but search is hidden on smaller screens) */}
            {showNavigation && isDataLoaded && (
              <div className="lg:hidden flex items-center space-x-1 bg-gray-50 rounded-lg p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="flex items-center gap-2 hover:bg-white transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Datei</span>
                </Button>
                
                <div className="h-6 w-px bg-gray-300 mx-2" />
                
                <div className="flex items-center gap-2 text-sm px-2">
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{quoteItems.length}</span>
                  </div>
                  
                  {totalNet > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-green-600 text-xs">
                        {new Intl.NumberFormat('de-DE', { 
                          style: 'currency', 
                          currency: 'EUR',
                          notation: 'compact'
                        }).format(totalGross)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Right side - Actions (darf bei schmalen Viewports umbrechen, Content folgt via --header-height-current) */}
            <div className="flex flex-wrap items-center justify-end gap-2">
              {/* New File Button - moved to right side */}
              {showNavigation && isDataLoaded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="hidden lg:flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span>Zurück</span>
                </Button>
              )}
              
              {/* Position Counter and Sort Reset - moved to right side */}
              {showNavigation && isDataLoaded && (
                <div className="hidden lg:flex items-center gap-3 pl-4 border-l border-gray-300">
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">{totalPositions}</span> Positionen
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    className="h-7 px-2 text-xs hover:bg-gray-100"
                    title="Alle Filter und Sortierung zurücksetzen"
                  >
                    <SortAsc className="h-3 w-3 mr-1" />
                    Reset
                  </Button>
                </div>
              )}
              
              {/* Blitzkalkulation Maskenwechsel — Mode-Toggle, keine primaere Aktion:
                  aktiv = secondary (mehr Gewicht), inaktiv = outline. Kein Gruen/Orange
                  als Aktionsfarbe (Welle 3.1 Farb-Rollen — Rot/Blau bleiben exklusiv). */}
              {showNavigation && isDataLoaded && !isGAEBDatabase && (
                <Button
                  variant={kalkulationsView === 'blitz' ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setKalkulationsView(kalkulationsView === 'blitz' ? 'standard' : 'blitz')}
                  className="gap-1.5"
                  title="Blitzkalkulation: Stundensatz und Material-Aufschlag einmal einstellen, dann nur noch Zeilen ausfüllen"
                >
                  <Zap className="h-4 w-4" />
                  <span className="hidden sm:inline">Blitzkalkulation</span>
                </Button>
              )}

              {/* Aufmaß Maskenwechsel — selbe Farb-Rolle wie Blitzkalkulation. */}
              {showNavigation && isDataLoaded && !isGAEBDatabase && (
                <Button
                  variant={aufmassView ? 'secondary' : 'outline'}
                  size="sm"
                  onClick={() => setAufmassView(!aufmassView)}
                  className="gap-1.5"
                  title="Aufmaß: verbaute Mengen je Position erfassen, Restmenge und Auslastung im Blick"
                >
                  <Ruler className="h-4 w-4" />
                  <span className="hidden sm:inline">Aufmaß</span>
                </Button>
              )}

              {/* Konto-/Mehr-Menü (Welle 1.5 UX-Entlastung — Kopfzeile entstapeln):
                  Einstellungen, Hilfe, SSO-Nutzerinfo und Abmelden gebündelt hinter
                  einem einzelnen Icon-Trigger (gleiches Bündelungs-Prinzip wie
                  "Export ▾" in quote-sidebar.tsx). Alle Aktionen bleiben unverändert
                  erreichbar (dieselben Handler/State) — nur gruppiert, nicht entfernt.
                  Immer sichtbar (nicht an showNavigation/isDataLoaded gekoppelt), da
                  Einstellungen/Hilfe/Abmelden vorher ebenfalls global sichtbar waren. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 transition-colors"
                    data-testid="button-account-menu-trigger"
                  >
                    <MoreVertical className="h-4 w-4" />
                    <span className="hidden sm:inline">Konto</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="menu-account">
                  {sessionVia === 'sso' && userName && (
                    <>
                      <DropdownMenuLabel className="flex items-center gap-1.5 font-normal text-gray-600">
                        <span className="truncate">{userName}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-blue-50 text-blue-700 border-blue-200">
                          SSO
                        </Badge>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem onSelect={() => setShowSettingsDialog(true)} data-testid="menuitem-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Einstellungen
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setShowHelpDialog(true)} data-testid="menuitem-help">
                    <HelpCircle className="mr-2 h-4 w-4" />
                    Hilfe
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => setShowLogoutDialog(true)} data-testid="menuitem-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Controlled Dialoge als GESCHWISTER des DropdownMenu (überleben das
                  Unmount des Menü-Contents beim Schließen — gleiches Muster wie
                  GAEBExport/X84PriceImport im Export-Menü, siehe quote-sidebar.tsx). */}
              <SettingsModal open={showSettingsDialog} onOpenChange={setShowSettingsDialog} />
              <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-center">
                      <div className="mb-2">
                        <h2 className="text-2xl font-bold text-primary">{companyName} Angebot & Rechnung</h2>
                        <p className="text-sm text-muted-foreground mt-1">Professionelle Angebots- und Rechnungserstellung v2.0</p>
                      </div>
                      <div className="text-lg font-medium text-gray-800">Anleitung</div>
                    </DialogTitle>
                    <DialogDescription>
                      So erstellen Sie Angebote und Rechnungen mit {companyName}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4 text-sm">
                    <div>
                      <h3 className="font-medium text-primary">📊 1. Startseite - Datenbanken & Angebote verwalten</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Farbcodierung (Source-Type Badges):</strong>
                        <br/>🔵 <strong>Manuell:</strong> Blau - Händisch erstellte Preislisten
                        <br/>🟢 <strong>Excel:</strong> Grün - Aus Excel importierte Preislisten
                        <br/>🟣 <strong>KI/Sprache:</strong> Lila - Per Spracheingabe erstellte Preislisten
                        <br/>🟠 <strong>X83:</strong> Orange - Importierte Ausschreibungen (X83 → X84 Workflow)
                        <br/><br/><strong>Aktionen (3-Punkte-Menü):</strong>
                        <br/>• <strong>Umbenennen:</strong> Namen direkt ändern
                        <br/>• <strong>Download:</strong> Als JSON oder Excel exportieren
                        <br/>• <strong>Löschen:</strong> Dauerhaft entfernen
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🔄 1.1 Angebot vs. Datenbank</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Datenbank:</strong> Wiederverwendbare Preisliste ohne Mengen (Katalog)
                        <br/><strong>Angebot:</strong> Spezifische Positionen mit Mengen für ein konkretes Projekt
                        <br/><strong>Beim Öffnen eines Angebots:</strong> Wählen Sie zwischen leerer Datenbank-Kopie oder Angebot mit Mengen
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">📁 2. Daten importieren</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Excel-Import:</strong> .xlsx, .xls, .csv, .ods Dateien direkt laden
                        <br/><strong>X83-Import:</strong> Ausschreibungen (.x83, .xml) importieren
                        <br/><strong>Automatische Erkennung:</strong> Spalten für Artikel-ID, Gewerk, Name, Beschreibung, Einheit, Preis
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🔍 3. Suchfunktion</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Allgemeine Suche:</strong> Durchsucht alle Felder gleichzeitig
                        <br/><strong>Name-Suche:</strong> Durchsucht nur die Name-Spalte für präzise Ergebnisse
                        <br/><strong>Echtzeit-Filterung:</strong> Ergebnisse werden sofort während der Eingabe aktualisiert
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🛒 4. Positionen auswählen</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Mengen eingeben:</strong> Geben Sie Werte direkt im Mengenfeld ein
                        <br/><strong>Dezimalzahlen:</strong> Bis zu 2 Nachkommastellen unterstützt
                        <br/><strong>Sofortige Berechnung:</strong> Preise und MwSt werden automatisch berechnet
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🏗️ 5. Gewerk-Organisation</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Automatische Gruppierung:</strong> Positionen werden nach Gewerken sortiert
                        <br/><strong>Drag & Drop:</strong> Reihenfolge der Gewerke in der Sidebar anpassbar
                        <br/><strong>Nummerierung:</strong> Gewerke erhalten automatisch fortlaufende Nummern
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">👤 6. Angebotsdaten & Kundensuche</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Kunde auswählen:</strong> Klicken Sie auf das Kunde-Feld um aus Softec zu suchen
                        <br/><strong>Auto-Vervollständigung:</strong> Adresse wird automatisch übernommen
                        <br/><strong>Manuelle Eingabe:</strong> Auch direkte Eingabe ohne Softec-Auswahl möglich
                        <br/><strong>Angebotsnummer:</strong> Automatische Generierung oder manuelle Eingabe
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">📄 7. Angebot exportieren</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>PDF Export:</strong> Professionelles PDF-Angebot mit eigener Vorlage
                        <br/><strong>Softec Export:</strong> Direkter Export zur Softec-App (Projekt + Budget)
                        <br/><strong>Excel/JSON Download:</strong> Über 3-Punkte-Menü auf der Startseite
                        <br/><strong>MwSt-Berechnung:</strong> Separate Ausweisung nach Steuersätzen
                      </p>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <h3 className="font-medium text-blue-800">💡 Tipps & Neue Features</h3>
                      <p className="text-blue-700 text-xs mt-1">
                        <strong>Kundensuche:</strong> Softec-Kunden direkt im Angebots-Formular suchen
                        <br/><strong>3-Punkte-Menü:</strong> Umbenennen, Download (JSON/Excel), Löschen
                        <br/><strong>Source-Type Badges:</strong> Erkenne sofort die Herkunft jeder Datenbank
                        <br/><strong>KI-Beschreibungen:</strong> Lila "Beschreibung erweitern" Button für detaillierte Texte
                        <br/><strong>X83-Editor:</strong> Preise eingeben und als X84-Angebot exportieren
                        <br/><strong>ESC-Taste:</strong> Schnell zur Startseite zurückkehren
                      </p>
                    </div>
                  </div>
                  <DialogClose asChild>
                    <Button className="mt-4">Verstanden</Button>
                  </DialogClose>
                </DialogContent>
              </Dialog>
            </div>
          </div>
          

          
          {/* Mobile search row - appears below table controls on mobile when data is loaded */}
          {showNavigation && isDataLoaded && (
            <div className="lg:hidden mt-3 pt-3 border-t border-gray-200">
              <div className="flex gap-2">
                {/* New File Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="flex items-center gap-1 hover:bg-blue-50 hover:border-blue-300 transition-colors flex-shrink-0"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Datei</span>
                  <span className="sm:hidden">Neu</span>
                </Button>
                
                {/* General Search */}
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Allgemeine Suche..."
                    className="pl-10 pr-10 h-9"
                    value={generalInputValue}
                    onChange={handleGeneralChange}
                  />
                  
                  {generalInputValue && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setGeneralInputValue('');
                          debouncedGeneralSearch('');
                        }}
                      >
                        <X className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Name-Only Search */}
                <div className="relative w-32 sm:w-40">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Tag className="h-4 w-4 text-blue-400" />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Name..."
                    className="pl-10 pr-10 border-blue-200 focus:border-blue-400 h-9"
                    value={nameInputValue}
                    onChange={handleNameChange}
                  />
                  
                  {nameInputValue && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setNameInputValue('');
                          debouncedNameSearch('');
                        }}
                      >
                        <X className="h-4 w-4 text-blue-400" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Reset Button */}
                {(generalInputValue || nameInputValue) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    className="px-2 h-9 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abmelden</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie sich wirklich abmelden?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => logout()}>Abmelden</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
